import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  DropZone,
  FormLayout,
  InlineStack,
  Layout,
  Modal,
  Page,
  ProgressBar,
  Select,
  Text,
  TextField,
  Thumbnail,
} from '@shopify/polaris';
import { NoteIcon } from '@shopify/polaris-icons';
import React, { useCallback, useEffect, useState } from 'react';
import agent, { AcesImportSetup, FitmentImportHistoryItem } from '../../app/api/agent';
import ImportHistoryCards from '../imports/ImportHistoryCards';

type UploadState = { file?: File; error?: string; success?: string; };
type AcesSetupState = {
  queueId: number;
  details: AcesImportSetup;
  selectedVendor: string;
  skuSuffixRule: string;
  useSkuSuffixRule: boolean;
  updateVendorRule: boolean;
  normalizeSku: boolean;
};

type VendorSkuRuleSettings = {
  skuPrefix: string;
  normalizeSku: boolean;
};

const noVendorOptionValue = '__NO_VENDOR__';

export default function PreviewImportsPage({ token, onUnauthorized, onLogout }: { token: string; onUnauthorized: () => void; onLogout: () => void; }) {
  const [uploadState, setUploadState] = useState<UploadState>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgressPercent, setUploadProgressPercent] = useState<number | undefined>();
  const [history, setHistory] = useState<FitmentImportHistoryItem[]>([]);
  const [historyError, setHistoryError] = useState<string | undefined>();
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [archivingIds, setArchivingIds] = useState<Record<number, boolean>>({});
  const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});
  const [isConfirmingAcesSetup, setIsConfirmingAcesSetup] = useState(false);
  const [acesSetup, setAcesSetup] = useState<AcesSetupState | undefined>();
  const [vendorSuffixRulesByVendor, setVendorSuffixRulesByVendor] = useState<Record<string, VendorSkuRuleSettings>>({});

  const parseError = useCallback((error: any, fallback: string) => {
    if (error?.response?.status === 401) onUnauthorized();
    return String(error?.response?.data || error?.message || fallback);
  }, [onUnauthorized]);

  const loadHistory = useCallback(async () => {
    try {
      setIsLoadingHistory(true);
      setHistoryError(undefined);
      setHistory(await agent.PreviewImports.history(token));
    } catch (error: any) {
      setHistoryError(parseError(error, 'Failed to load import history.'));
    } finally {
      setIsLoadingHistory(false);
    }
  }, [parseError, token]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadVendorSuffixRules = useCallback(async () => {
    try {
      const rules = await agent.PreviewImports.vendorSkuRules(token);
      setVendorSuffixRulesByVendor(rules.reduce<Record<string, VendorSkuRuleSettings>>((accumulator, rule) => {
        accumulator[rule.shopifyVendor] = {
          skuPrefix: rule.skuPrefix || '',
          normalizeSku: rule.normalizeSku === true,
        };
        return accumulator;
      }, {}));
    } catch {
      setVendorSuffixRulesByVendor({});
    }
  }, [token]);

  useEffect(() => { if (acesSetup?.queueId) loadVendorSuffixRules(); }, [acesSetup?.queueId, loadVendorSuffixRules]);

  useEffect(() => {
    if (!acesSetup?.selectedVendor) return;
    const vendorRule = vendorSuffixRulesByVendor[acesSetup.selectedVendor];
    if (vendorRule === undefined) return;

    setAcesSetup((previous) => {
      if (!previous || previous.selectedVendor !== acesSetup.selectedVendor || (previous.skuSuffixRule === vendorRule.skuPrefix && previous.normalizeSku === vendorRule.normalizeSku)) return previous;
      return { ...previous, skuSuffixRule: vendorRule.skuPrefix, normalizeSku: vendorRule.normalizeSku };
    });
  }, [acesSetup?.selectedVendor, vendorSuffixRulesByVendor]);

  const handleDropZoneDrop = useCallback((_: File[], acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return setUploadState({ file: undefined, error: 'No file selected.' });

    const extension = selectedFile.name.includes('.') ? selectedFile.name.split('.').pop()?.toLowerCase() : '';
    if (extension !== 'xml') {
      return setUploadState({ file: undefined, success: undefined, error: 'Invalid file type. Select a .xml file.' });
    }

    setUploadProgressPercent(undefined);
    setUploadState({ file: selectedFile, error: undefined, success: undefined });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!uploadState.file) return setUploadState((prev) => ({ ...prev, error: 'Please select a file to upload.' }));

    try {
      setIsSubmitting(true);
      setUploadProgressPercent(0);
      const result = await agent.PreviewImports.queue(token, uploadState.file, (progressEvent) => {
        if (!progressEvent.total || progressEvent.total <= 0) {
          return;
        }

        const percent = Math.min(100, Math.round((progressEvent.loaded / progressEvent.total) * 100));
        setUploadProgressPercent(percent);
      });
      if (result.acesSetup) {
        const details = result.acesSetup;
        setAcesSetup({
          queueId: result.queueId,
          details,
          selectedVendor: details.matchedVendor || '',
          skuSuffixRule: details.vendorSkuSuffixRule || '',
          useSkuSuffixRule: true,
          updateVendorRule: true,
          normalizeSku: details.normalizeSku === true,
        });
        setUploadState({ file: undefined, error: undefined, success: 'ACES header parsed. Review vendor and SKU mapping settings, then start the import.' });
      } else {
        setUploadState({ file: undefined, error: undefined, success: 'Your import has been scheduled for processing.' });
      }
      await loadHistory();
    } catch (error: any) {
      setUploadState((prev) => ({ ...prev, success: undefined, error: parseError(error, 'Failed to queue import.') }));
    } finally {
      setIsSubmitting(false);
      setUploadProgressPercent(undefined);
    }
  }, [loadHistory, parseError, token, uploadState.file]);

  const handleConfirmAcesSetup = useCallback(async () => {
    if (!acesSetup) return;

    try {
      setIsConfirmingAcesSetup(true);
      const hasVendorSelection = !!acesSetup.selectedVendor;
      await agent.PreviewImports.configureAces(token, acesSetup.queueId, {
        selectedVendor: hasVendorSelection ? acesSetup.selectedVendor : undefined,
        skuSuffixRule: hasVendorSelection ? acesSetup.skuSuffixRule : undefined,
        useSkuSuffixRule: hasVendorSelection && acesSetup.useSkuSuffixRule,
        updateVendorRule: hasVendorSelection && acesSetup.updateVendorRule,
        normalizeSku: hasVendorSelection && acesSetup.normalizeSku,
      });
      setUploadState({ file: undefined, error: undefined, success: 'ACES import configured and queued for processing.' });
      setAcesSetup(undefined);
      await loadHistory();
    } catch (error: any) {
      setUploadState((prev) => ({ ...prev, success: undefined, error: parseError(error, 'Failed to configure ACES import.') }));
    } finally {
      setIsConfirmingAcesSetup(false);
    }
  }, [acesSetup, loadHistory, parseError, token]);

  const handleArchive = useCallback(async (queueId: number) => {
    try {
      setArchivingIds((prev) => ({ ...prev, [queueId]: true }));
      await agent.PreviewImports.archive(token, queueId);
      setHistory((prev) => prev.filter((item) => item.queueId !== queueId));
    } catch (error: any) {
      setHistoryError(parseError(error, 'Failed to archive import history item.'));
    } finally {
      setArchivingIds((prev) => ({ ...prev, [queueId]: false }));
    }
  }, [parseError, token]);

  const handleDownload = useCallback(async (item: FitmentImportHistoryItem) => {
    try {
      setDownloadingIds((prev) => ({ ...prev, [item.queueId]: true }));
      const response = await agent.PreviewImports.download(token, item.queueId);
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = item.originalFileName || `import-${item.queueId}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      setHistoryError(parseError(error, 'Failed to download import file.'));
    } finally {
      setDownloadingIds((prev) => ({ ...prev, [item.queueId]: false }));
    }
  }, [parseError, token]);

  const fileUpload = !uploadState.file && <DropZone.FileUpload actionHint="Accepted file types: .xml" />;
  const uploadedFile = uploadState.file && (
    <InlineStack gap="300" align="start" blockAlign="center">
      <Thumbnail size="small" alt={uploadState.file.name} source={NoteIcon} />
      <div>
        <Text as="p" variant="bodyMd">{uploadState.file.name}</Text>
        <Text as="p" variant="bodySm" tone="subdued">{uploadState.file.size} bytes</Text>
      </div>
    </InlineStack>
  );

  return (
    <Page title="PartFits Preview Console" subtitle="Alpha access for ACES imports" secondaryActions={[{ content: "Logout", onAction: onLogout }]}>
      <Layout>
        <Layout.Section>
          <Card>
            <FormLayout>
              <DropZone allowMultiple={false} onDrop={handleDropZoneDrop}>{uploadedFile}{fileUpload}</DropZone>
              {uploadState.error && <Banner tone="critical" title="Upload validation failed"><p>{uploadState.error}</p></Banner>}
              {uploadState.success && <Banner tone="success" title="Import queued"><p>{uploadState.success}</p></Banner>}

              {isSubmitting && uploadProgressPercent !== undefined && (
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">Uploading file… {uploadProgressPercent}%</Text>
                  <ProgressBar progress={uploadProgressPercent} size="small" />
                </BlockStack>
              )}
              <InlineStack align="end"><Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>Submit File</Button></InlineStack>
            </FormLayout>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between"><Text as="h2" variant="headingMd">Import history</Text><Button onClick={loadHistory} loading={isLoadingHistory}>Refresh</Button></InlineStack>
              {historyError && <Banner tone="critical" title="Unable to load import history"><p>{historyError}</p></Banner>}
              {history.length === 0 && !isLoadingHistory ? <Text as="p" tone="subdued">No import history yet.</Text> : (
                <BlockStack gap="200">
                  <ImportHistoryCards items={history.slice(0, 5)} archivingIds={archivingIds} downloadingIds={downloadingIds} onArchive={handleArchive} onDownload={handleDownload} />
                  <Box paddingBlockStart="200"><InlineStack align="center"><Text as="p" tone="subdued">Showing 5 most recent imports</Text></InlineStack></Box>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={!!acesSetup}
        onClose={() => !isConfirmingAcesSetup && setAcesSetup(undefined)}
        title="Finalize ACES import"
        primaryAction={{ content: 'Start ACES import', onAction: handleConfirmAcesSetup, loading: isConfirmingAcesSetup }}
        secondaryActions={[{ content: 'Cancel', onAction: () => !isConfirmingAcesSetup && setAcesSetup(undefined) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" tone="subdued">Confirm vendor mapping before fitment rows are processed.</Text>
            <Text as="p" variant="bodySm">{acesSetup?.details.documentTitle || 'Untitled ACES document'} {acesSetup?.details.company ? `· ${acesSetup.details.company}` : ''}</Text>
            <Select
              label="Vendor (optional)"
              options={[{ label: 'No vendor (match store-wide)', value: noVendorOptionValue }, ...(acesSetup?.details.vendorOptions.map((v) => ({ label: v, value: v })) ?? [])]}
              value={acesSetup?.selectedVendor || noVendorOptionValue}
              onChange={(value) => setAcesSetup((previous) => {
                if (!previous) return previous;
                const selectedVendor = value === noVendorOptionValue ? '' : value;
                return {
                  ...previous,
                  selectedVendor,
                  skuSuffixRule: selectedVendor ? (vendorSuffixRulesByVendor[selectedVendor]?.skuPrefix ?? '') : '',
                  useSkuSuffixRule: selectedVendor ? previous.useSkuSuffixRule : false,
                  updateVendorRule: selectedVendor ? previous.updateVendorRule : false,
                  normalizeSku: selectedVendor ? (vendorSuffixRulesByVendor[selectedVendor]?.normalizeSku ?? false) : false,
                };
              })}
              helpText="Leave vendor unselected to match SKUs across the entire store."
            />
            <TextField label="Vendor SKU suffix rule" value={acesSetup?.skuSuffixRule ?? ''} onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, skuSuffixRule: value } : previous)} autoComplete="off" disabled={!acesSetup?.selectedVendor} helpText="Adds this suffix when matching ACES part numbers to Shopify SKUs. Falls back to the original part number if no match is found." />
            <Checkbox label="Use this suffix rule for this import" checked={acesSetup?.useSkuSuffixRule ?? false} onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, useSkuSuffixRule: value } : previous)} disabled={!acesSetup?.selectedVendor} />
            <Checkbox label="Normalize SKU (remove special characters before matching)" checked={acesSetup?.normalizeSku ?? false} onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, normalizeSku: value } : previous)} disabled={!acesSetup?.selectedVendor} />
            <Checkbox label="Update saved vendor rule with this value" checked={acesSetup?.updateVendorRule ?? false} onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, updateVendorRule: value } : previous)} disabled={!acesSetup?.selectedVendor} />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
